import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui';
import FileUpload from '@/components/ui/FileUpload';
import { WebsitePageSection } from '@/types';
import { showToast, toast } from '@/lib/toast';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import UploadIcon from '@/assets/icons/UploadIcon';
import { Paragraph } from '@/components/ui/Typography';
import { HeroImageFormDataSchema } from '@/components/Modals/EditContentsModals/EditContentsSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

interface InlineImageEditorProps {
  data: WebsitePageSection;
  onSave: (updatedData: Partial<WebsitePageSection>) => void;
  onCancel: () => void;
}

const InlineImageEditor: React.FC<InlineImageEditorProps> = ({ data, onSave, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [currentFileUrl, setCurrentFileUrl] = useState<string>(data.fileUrl ?? '');
  const [imageDimensions, setImageDimensions] = useState<string>('N/A');
  const [uploadedAt, setUploadedAt] = useState<string>('N/A');
  const fileUploadRef = useRef<HTMLDivElement>(null);
  const fileUploadMutation = useFileUpload();

  const {
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(HeroImageFormDataSchema),
    defaultValues: {
      fileUrl: data.fileUrl ?? ''
    },
    mode: 'all'
  });

  useEffect(() => {
    setPreviewUrl(null);
    setUploadedFile(null);
    setCurrentFileUrl(data.fileUrl ?? '');
    setImageDimensions('N/A');
    setUploadedAt('N/A');
  }, [data]);

  const formatFileSize = (bytes: number): string => {
    if (!bytes || Number.isNaN(bytes)) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatUploadedAt = (date: Date): string => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getFileNameFromUrl = (url: string): string => {
    try {
      const pathname = new URL(url).pathname;
      const fileName = pathname.split('/').pop() || 'image';
      return decodeURIComponent(fileName);
    } catch {
      const fileName = url.split('/').pop() || 'image';
      return decodeURIComponent(fileName);
    }
  };

  const loadImageDimensions = (src: string) => {
    const image = new window.Image();
    image.onload = () => {
      setImageDimensions(`${image.naturalWidth} x ${image.naturalHeight} px`);
    };
    image.onerror = () => setImageDimensions('N/A');
    image.src = src;
  };

  useEffect(() => {
    if (previewUrl) {
      loadImageDimensions(previewUrl);
      setUploadedAt(formatUploadedAt(new Date()));
      return;
    }

    if (currentFileUrl) {
      loadImageDimensions(currentFileUrl);
    }
  }, [previewUrl, currentFileUrl]);

  const handleBrowseFile = () => {
    fileUploadRef.current?.click();
  };

  const handleReplaceImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setUploadedFile(null);
    setPreviewUrl(null);
    setCurrentFileUrl('');
    setImageDimensions('N/A');
    setUploadedAt('N/A');
    setValue('fileUrl', '', { shouldValidate: true });
  };

  const handleFileChange = (files: File[] | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setUploadedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setValue('fileUrl', objectUrl, { shouldValidate: true });
    } else {
      setUploadedFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
      setValue('fileUrl', currentFileUrl, { shouldValidate: true });
    }
  };

  const handleSave = async () => {
    if (!data.sectionTag) {
      showToast.error('Section tag not found.');
      return;
    }

    const uploadFile = async (file: File) => {
      setIsLoading(true);
      try {
        const response = await fileUploadMutation.mutateAsync({ file });
        return response?.data?.fileUrl ?? '';
      } catch (error) {
        setIsLoading(false);
        console.error('File upload failed:', error);
        toast.error('File upload failed. Please try again.');
        return null;
      }
    };

    // Only upload if there's a new file, otherwise use existing fileUrl
    let fileUrl = currentFileUrl || '';
    if (uploadedFile) {
      const uploadedFileUrl = await uploadFile(uploadedFile);
      if (!uploadedFileUrl) {
        setIsLoading(false);
        return;
      }
      fileUrl = uploadedFileUrl;
    }

    try {
      onSave({
        fileUrl: fileUrl,
        contentType: 'WITH_FILE_ONLY'
      });
      setCurrentFileUrl(fileUrl);
      
      // Clean up preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setUploadedFile(null);
    } catch (error) {
      setIsLoading(false);
      showToast.error('An unexpected error occurred.');
      console.error('Update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setUploadedFile(null);
    onCancel();
  };

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const displayUrl = previewUrl || currentFileUrl;
  const currentFileName = uploadedFile?.name || (currentFileUrl ? getFileNameFromUrl(currentFileUrl) : 'N/A');
  const currentFileSize = uploadedFile ? formatFileSize(uploadedFile.size) : 'N/A';

  return (
    <div className="flex flex-col gap-4">
      {/* Image Preview */}
      {displayUrl && (
        <div className="relative w-full rounded-lg overflow-hidden border border-gray-200">
          <img
            src={displayUrl}
            alt="Preview"
            className="w-full h-64 object-cover"
          />
        </div>
      )}

      {/* File Upload + Current Image */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <FileUpload
            ref={fileUploadRef}
            label=""
            placeholder=""
            $maxSizeMB={5}
            error={errors.fileUrl?.message}
            multiple={false}
            showPreview={false}
            allowedFileTypes={['image/*']}
            containerClassName="border border-[#E5E5E5] bg-white"
            $dataId="inline-image-upload"
            onChange={handleFileChange}
          >
            <div className="flex flex-col items-center justify-center gap-4">
              <UploadIcon stroke="#00764B" />
              <Paragraph className="font-semibold text-primary">
                Click or drag and drop file to upload
              </Paragraph>
              <Paragraph className="text-sm text-text-secondary">
                Supported document file is PNG, & JPEG with max size of 5mb
              </Paragraph>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {uploadedFile && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReplaceImage();
                    }}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Replace Image
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBrowseFile();
                  }}
                  className="bg-[#ECFEF8] text-[#00764B] hover:bg-[#ECFEF8] hover:text-[#000] px-4 py-2 rounded-md font-semibold"
                >
                  Browse File
                </button>
              </div>
            </div>
          </FileUpload>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h4 className="mb-4 text-lg font-semibold text-gray-900">Current Image</h4>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-gray-500">File name</span>
              <span className="max-w-[60%] truncate text-right text-gray-900" title={currentFileName}>{currentFileName}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-gray-500">Dimensions</span>
              <span className="text-right text-gray-900">{imageDimensions}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-gray-500">File size</span>
              <span className="text-right text-gray-900">{currentFileSize}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-gray-500">Uploaded</span>
              <span className="text-right text-gray-900">{uploadedAt}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="secondary"
          onClick={handleCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading || (!uploadedFile && !currentFileUrl)}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default InlineImageEditor;

